<template>
  <div class="flex flex-col bg-[#f8fafc] gap-4">
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-[#0d4169] flex flex-col pt-3">
      <div class="relative z-30 px-6 pb-0 flex flex-col gap-4 border-b border-slate-100 bg-white max-md:overflow-visible">
        <h2 class="text-base font-bold text-[#0d4169] text-left m-0">Finance</h2>
        <div
          ref="tabsBarRef"
          class="finance-tabs-bar relative mt-0 pt-0 -mb-[1px] max-md:-mx-6 max-md:px-6"
        >
          <div class="finance-tabs-scroll flex gap-4 md:gap-6 max-md:overflow-x-auto max-md:flex-nowrap">
            <template v-for="tab in visibleTabs" :key="tab.key">
            <div v-if="tab.type === 'dropdown'" class="relative max-md:shrink-0">
              <button
                type="button"
                @click="toggleDropdownTab(tab.key)"
                :class="{
                  'border-[#0d4169] text-[#0d4169]': activeTab === tab.key,
                  'border-transparent text-slate-500': activeTab !== tab.key
                }"
                class="pb-2 text-sm font-bold border-b-2 hover:text-[#0d4169] transition-colors inline-flex items-center gap-1 max-md:whitespace-nowrap max-md:shrink-0"
              >
                <span class="max-md:whitespace-nowrap">{{ tab.label }}</span>
                <svg
                  class="w-3 h-3 shrink-0 transition-transform duration-200"
                  :class="isDropdownOpen(tab.key) ? 'rotate-180' : ''"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>

              <div
                v-if="isDropdownOpen(tab.key)"
                class="hidden md:block absolute left-0 top-full mt-2 w-52 rounded-md border border-slate-200 bg-white shadow-lg z-50"
              >
                <button
                  v-for="item in getDropdownItems(tab.key)"
                  :key="typeof item === 'object' ? item.key : item"
                  type="button"
                  :disabled="isDropdownItemDisabled(tab.key, item)"
                  @click="onDropdownItemClick(tab.key, item)"
                  :class="[
                    'w-full px-3 py-2 text-left text-xs font-semibold border-b border-slate-100 last:border-b-0 transition-colors',
                    isDropdownItemDisabled(tab.key, item)
                      ? 'text-slate-400 cursor-not-allowed opacity-60'
                      : 'text-slate-700 hover:bg-blue-600 hover:text-white'
                  ]"
                >
                  {{ typeof item === 'object' ? item.label : item }}
                </button>
              </div>
            </div>

            <button
              v-else
              type="button"
              @click="selectTab(tab.key)"
              :class="{
                'border-[#0d4169] text-[#0d4169]': activeTab === tab.key,
                'border-transparent text-slate-500': activeTab !== tab.key
              }"
              class="pb-2 text-sm font-bold border-b-2 hover:text-[#0d4169] transition-colors max-md:whitespace-nowrap max-md:shrink-0"
            >
              {{ tab.label }}
            </button>
            </template>
          </div>

          <template v-for="tab in visibleTabs" :key="`mobile-dropdown-${tab.key}`">
            <div
              v-if="tab.type === 'dropdown' && isDropdownOpen(tab.key)"
              class="finance-tabs-mobile-dropdown md:hidden absolute left-0 right-0 top-full z-[60] flex justify-center px-4 pt-2"
            >
              <div class="w-52 max-w-[calc(100vw-3rem)] rounded-md border border-slate-200 bg-white shadow-lg">
                <button
                  v-for="item in getDropdownItems(tab.key)"
                  :key="typeof item === 'object' ? item.key : item"
                  type="button"
                  :disabled="isDropdownItemDisabled(tab.key, item)"
                  @click="onDropdownItemClick(tab.key, item)"
                  :class="[
                    'w-full px-3 py-2 text-left text-xs font-semibold border-b border-slate-100 last:border-b-0 transition-colors',
                    isDropdownItemDisabled(tab.key, item)
                      ? 'text-slate-400 cursor-not-allowed opacity-60'
                      : 'text-slate-700 hover:bg-blue-600 hover:text-white'
                  ]"
                >
                  {{ typeof item === 'object' ? item.label : item }}
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="relative z-0 px-6 py-4">
        <CompanyProfile v-if="activeTab === 'register' && activeRegisterView === 'company_profile'" />
        <IncomeCategory v-else-if="activeTab === 'register' && activeRegisterView === 'income_category'" />
        <CostCategory v-else-if="activeTab === 'register' && activeRegisterView === 'cost_category'" />
        <ChartOfAccount v-else-if="activeTab === 'register' && activeRegisterView === 'chart_of_account'" />
        <ItemsClassify v-else-if="activeTab === 'register' && activeRegisterView === 'items_classify'" />
        <div
          v-else-if="activeTab === 'register' && activeRegisterView === 'pension_and_tax'"
          class="flex flex-col gap-4"
        >
          <PensionPage v-if="canShowFinancePension" :hide-header="true" />
          <IncomeTax v-if="canShowFinanceIncomeTax" :hide-header="true" />
        </div>
        <WithholdingPage v-else-if="activeTab === 'register' && activeRegisterView === 'withholding'" />
        <OvertimePage v-else-if="activeTab === 'register' && activeRegisterView === 'overtime'" />
        <EarnDeductType v-else-if="activeTab === 'register' && activeRegisterView === 'earn_deduct_type'" />
        <BankRegister v-else-if="activeTab === 'register' && activeRegisterView === 'bank_register'" />

        <IncomePage v-else-if="activeTab === 'data_posting' && activeDataPostingView === 'income'" />
        <WithholdingReceivedPage v-else-if="activeTab === 'data_posting' && activeDataPostingView === 'withholding_received'" />
        <WithholdingIssuePage v-else-if="activeTab === 'data_posting' && activeDataPostingView === 'withholding_issue'" />
        <PaymentPurchasePage v-else-if="activeTab === 'data_posting' && activeDataPostingView === 'payment_purchase'" />
        <SalesByTeamPage v-else-if="activeTab === 'data_posting' && activeDataPostingView === 'sales_by_team'" />
        <FinanceInventoryPage v-else-if="activeTab === 'data_posting' && activeDataPostingView === 'finance_inventory'" />
        <BankReconciliationPage v-else-if="activeTab === 'data_posting' && activeDataPostingView === 'bank_reconciliation'" />

        <EarnAndDeduct v-else-if="activeTab === 'payroll' && activePayrollView === 'monthly_configuration'" />
        <PayrollPage
          v-else-if="activeTab === 'payroll' && activePayrollView === 'payroll_list'"
          change-page-name="Payroll List"
          :hide-date-range="true"
        />

        <div v-else class="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center">
          <h3 class="text-sm font-bold text-slate-700 mb-2">
            {{ contentPlaceholderHeading }}
          </h3>
          <p class="text-xs text-slate-500">Select from the list</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useStore } from 'vuex'
import { apiFetch } from '../../utils/api'
import CompanyProfile from './register/companyProfile.vue'
import IncomeCategory from './register/incomeCategory.vue'
import CostCategory from './register/costCategory.vue'
import ChartOfAccount from './register/chartOfAccount.vue'
import ItemsClassify from './register/itemsClassify.vue'
import IncomeTax from './advancedRegister/incomeTax.vue'
import PensionPage from './advancedRegister/pensionPage.vue'
import WithholdingPage from './advancedRegister/withholdingPage.vue'
import OvertimePage from './advancedRegister/overtimePage.vue'
import EarnDeductType from './advancedRegister/earnDeductType.vue'
import BankRegister from './advancedRegister/bankRegister.vue'
import IncomePage from './dataPosting/incomePage.vue'
import WithholdingReceivedPage from './dataPosting/withholdingReceived.vue'
import WithholdingIssuePage from './dataPosting/withholdingIssue.vue'
import PaymentPurchasePage from './dataPosting/paymentPurchase.vue'
import SalesByTeamPage from './dataPosting/salesByTeam.vue'
import FinanceInventoryPage from './dataPosting/financeInventory.vue'
import BankReconciliationPage from './dataPosting/bankReconciliation.vue'
import EarnAndDeduct from './monthlyConfiguration/earnAndDeduct.vue'
import PayrollPage from './payroll/payrollPage.vue'

const store = useStore()

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAdminPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function hasAnyActionPrivilege(privObj) {
  if (!privObj || typeof privObj !== 'object') return false
  return !!(privObj.Add || privObj.Edit || privObj.View || privObj.Void || privObj.Approve)
}

function hasAddEditViewPrivilege(privObj) {
  if (!privObj || typeof privObj !== 'object') return false
  return !!(privObj.Add || privObj.Edit || privObj.View)
}

function hasPayrollWorkflowPrivilege(privObj) {
  if (!privObj || typeof privObj !== 'object') return false
  return !!(
    privObj['Prepared By'] ||
    privObj['Checked By'] ||
    privObj['Cross Checked By'] ||
    privObj['Approved By']
  )
}

function hasMonthlyConfigurationPrivilege() {
  return (
    hasAddEditViewPrivilege(getPrivilegeBlock('Monthly Configuration')) ||
    hasPayrollWorkflowPrivilege(getPrivilegeBlock('Earn And Deduct')) ||
    hasPayrollWorkflowPrivilege(getPrivilegeBlock('Attendance'))
  )
}

function hasPayrollListPrivilege() {
  return (
    hasAddEditViewPrivilege(getPrivilegeBlock('Payroll List')) ||
    hasPayrollWorkflowPrivilege(getPrivilegeBlock('Payroll List'))
  )
}

function getPrivilegeBlock(name) {
  const privs = userPrivileges.value || {}
  const exact = privs?.[name]
  if (exact) return exact
  const lower = privs?.[String(name).toLowerCase()]
  return lower || null
}

function parseModuleList(value) {
  if (!value) return []
  return String(value)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
}

function moduleListIncludes(modules, moduleName) {
  return parseModuleList(modules).includes(moduleName)
}

function resolveUserModules(user) {
  if (!user) return null
  const rawBranchId = user.branchId ?? user.branch_id ?? user.branch?.id ?? null
  const hasBranchId = rawBranchId != null && rawBranchId !== '' && Number(rawBranchId) > 0
  return hasBranchId ? user.branch?.modules : user.company?.modules
}

const hasHrModuleAccess = computed(() => {
  return moduleListIncludes(resolveUserModules(currentUser.value), 'HR')
})

const canShowFinancePension = computed(() => {
  if (hasAdminPrivilege.value) return true
  return hasAnyActionPrivilege(getPrivilegeBlock('Pension'))
})

const canShowFinanceIncomeTax = computed(() => {
  if (hasAdminPrivilege.value) return true
  return hasAnyActionPrivilege(getPrivilegeBlock('Income Tax'))
})

const registerDropdownItems = [
  { key: 'company_profile', label: 'Company Profile' },
  // { key: 'income_category', label: 'Income Category' },
  // { key: 'cost_category', label: 'Cost Category' },
  { key: 'chart_of_account', label: 'Chart of Account' },
  { key: 'pension_and_tax', label: 'Pension And TAX' },
  { key: 'items_classify', label: 'Items Classify' },
  { key: 'withholding', label: 'Withholding' },
  // { key: 'overtime', label: 'Overtime' },
  // { key: 'earn_deduct_type', label: 'Earn And Deduct Type' },
  { key: 'bank_register', label: 'Bank Register' },
 
]

const hasRegisterTabPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  const financePriv = getPrivilegeBlock('Finance')
  const registerFinancePriv = getPrivilegeBlock('Finance Register')
  const advancedRegisterPriv = getPrivilegeBlock('Advanced Register')
  return (
    hasAnyActionPrivilege(financePriv) ||
    hasAnyActionPrivilege(registerFinancePriv) ||
    hasAnyActionPrivilege(advancedRegisterPriv)
  )
})

const visibleRegisterDropdownItems = computed(() => {
  if (!hasRegisterTabPrivilege.value) return []

  const items = registerDropdownItems.filter((item) => {
    // Pension And TAX stays in Finance Register only when HR module is not available
    if (item.key === 'pension_and_tax') return !hasHrModuleAccess.value
    return true
  })

  if (hasAdminPrivilege.value) return items

  const registerPrivilegeMap = {
    company_profile: 'Company Profile',
    income_category: 'Income Category',
    cost_category: 'Cost Category',
    chart_of_account: 'Chart of Account',
    items_classify: 'Items Classify',
    withholding: 'Withholding',
    overtime: 'Overtime',
    earn_deduct_type: 'Earn And Deduct Type',
    bank_register: 'Bank Register'
  }

  return items.filter((item) => {
    if (item.key === 'pension_and_tax') {
      return canShowFinanceIncomeTax.value || canShowFinancePension.value
    }
    const privilegeName = registerPrivilegeMap[item.key]
    return hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))
  })
})

const advancedRegisterDropdownItems = [
  { key: 'income_tax', label: 'Income Tax' },
  { key: 'pension', label: 'Pension' },
  { key: 'withholding', label: 'Withholding' },
  { key: 'overtime', label: 'Overtime' },
  { key: 'earn_deduct_type', label: 'Earn And Deduct Type' },
  { key: 'bank_register', label: 'Bank Register' }
]

const hasAdvancedRegisterTabPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  const financePriv = getPrivilegeBlock('Finance')
  const advancedRegisterPriv = getPrivilegeBlock('Advanced Register')
  return hasAnyActionPrivilege(financePriv) || hasAnyActionPrivilege(advancedRegisterPriv)
})

const visibleAdvancedRegisterDropdownItems = computed(() => {
  if (!hasAdvancedRegisterTabPrivilege.value) return []
  if (hasAdminPrivilege.value) return advancedRegisterDropdownItems

  const advancedRegisterPrivilegeMap = {
    income_tax: 'Income Tax',
    pension: 'Pension',
    withholding: 'Withholding',
    overtime: 'Overtime',
    earn_deduct_type: 'Earn And Deduct Type',
    bank_register: 'Bank Register'
  }

  return advancedRegisterDropdownItems.filter((item) => {
    const privilegeName = advancedRegisterPrivilegeMap[item.key]
    return hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))
  })
})

const hasDataPostingTabPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  const financePriv = getPrivilegeBlock('Finance')
  const dataPostingPriv = getPrivilegeBlock('Data Posting')
  return hasAnyActionPrivilege(financePriv) || hasAnyActionPrivilege(dataPostingPriv)
})

const visibleDataPostingDropdownItems = computed(() => {
  if (!hasDataPostingTabPrivilege.value) return []
  if (hasAdminPrivilege.value) return dataPostingDropdownItems

  const dataPostingPrivilegeMap = {
    income: 'Income',
    withholding_received: 'Withholding Received',
    withholding_issue: 'Withholding Issue',
    payment_purchase: 'Payment/ Purchase',
    sales_by_team: 'Sales By TIN',
    finance_inventory: 'Finance Inventory',
    bank_reconciliation: 'Bank Reconciliation'
  }

  return dataPostingDropdownItems.filter((item) => {
    const privilegeName = dataPostingPrivilegeMap[item.key]
    return hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))
  })
})

const dataPostingDropdownItems = [
  { key: 'income', label: 'Income' },
  { key: 'withholding_received', label: 'Withholding Received' },
  { key: 'withholding_issue', label: 'Withholding Issue' },
  { key: 'payment_purchase', label: 'Payment/ Purchase' },
  { key: 'sales_by_team', label: 'Sales By TIN' },
  { key: 'finance_inventory', label: 'Finance Inventory' },
  { key: 'bank_reconciliation', label: 'Bank Reconciliation' }
]

const payrollDropdownItems = [
  // { key: 'monthly_configuration', label: 'Monthly Configuration' },
  { key: 'payroll_list', label: 'Payroll List' }
]

const hasPayrollTabPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  // Only show Payroll tab when user has Payroll (Add/Edit/View) explicitly
  return hasAddEditViewPrivilege(getPrivilegeBlock('Payroll'))
})

const visiblePayrollDropdownItems = computed(() => {
  if (!hasPayrollTabPrivilege.value) return []
  if (hasAdminPrivilege.value) return payrollDropdownItems

  const payrollPrivilegeMap = {
    monthly_configuration: hasMonthlyConfigurationPrivilege,
    payroll_list: hasPayrollListPrivilege
  }

  return payrollDropdownItems.filter((item) => {
    const checkPrivilege = payrollPrivilegeMap[item.key]
    return typeof checkPrivilege === 'function' ? checkPrivilege() : false
  })
})

const tabs = [
  { key: 'register', label: 'Register', type: 'dropdown', items: registerDropdownItems },
  // { key: 'advanced_register', label: 'Advanced register', type: 'dropdown', items: advancedRegisterDropdownItems },
  { key: 'data_posting', label: 'Data Posting', type: 'dropdown', items: dataPostingDropdownItems },
  { key: 'payroll', label: 'Payroll', type: 'dropdown', items: payrollDropdownItems }
]

const visibleTabs = computed(() => {
  return tabs.filter((tab) => {
    if (tab.key === 'register') {
      return hasRegisterTabPrivilege.value && visibleRegisterDropdownItems.value.length > 0
    }
    // if (tab.key === 'advanced_register') {
    //   return hasAdvancedRegisterTabPrivilege.value && visibleAdvancedRegisterDropdownItems.value.length > 0
    // }
    if (tab.key === 'data_posting') {
      return hasDataPostingTabPrivilege.value && visibleDataPostingDropdownItems.value.length > 0
    }
    if (tab.key === 'payroll') {
      return hasPayrollTabPrivilege.value && visiblePayrollDropdownItems.value.length > 0
    }
    return true
  })
})

const activeTab = ref('register')
const showRegisterDropdown = ref(false)
const showAdvancedRegisterDropdown = ref(false)
const showDataPostingDropdown = ref(false)
const showPayrollDropdown = ref(false)
const activeRegisterView = ref(null)
const activeAdvancedRegisterView = ref(null)
const activeDataPostingView = ref(null)
const activePayrollView = ref(null)
const tabsBarRef = ref(null)
const companyStatus = ref('')

const isCompanySol = computed(() => companyStatus.value === 'SOL')

async function loadCompanyProfile() {
  try {
    const response = await apiFetch('/finance/company-profile')
    if (!response.ok) return
    const parsed = await response.json().catch(() => null)
    if (!parsed || typeof parsed !== 'object') return
    companyStatus.value = String(parsed.companyStatus || '').trim()
  } catch {
    // Leave companyStatus empty; withholding issue stays available if profile cannot be loaded.
  }
}

function isDropdownItemDisabled(tabKey, item) {
  if (tabKey !== 'data_posting' || !item || typeof item !== 'object') return false
  return item.key === 'withholding_issue' && isCompanySol.value
}

watch(
  companyStatus,
  (status) => {
    if (status === 'SOL' && activeDataPostingView.value === 'withholding_issue') {
      activeDataPostingView.value = null
    }
  }
)

watch(hasHrModuleAccess, (hasHr) => {
  if (
    hasHr &&
    (activeRegisterView.value === 'pension_and_tax' ||
      activeRegisterView.value === 'income_tax' ||
      activeRegisterView.value === 'pension')
  ) {
    activeRegisterView.value = null
  }
})

watch(
  visibleTabs,
  (newTabs) => {
    if (!newTabs?.length) return
    if (!newTabs.some((t) => t.key === activeTab.value)) {
      // If the currently selected tab becomes hidden by privileges, switch to the first visible tab.
      selectTab(newTabs[0].key)
    }
  },
  { immediate: true }
)

const contentPlaceholderHeading = computed(() => {
  if (activeTab.value === 'register') {
    if (activeRegisterView.value) {
      const item = registerDropdownItems.find((i) => i.key === activeRegisterView.value)
      return item?.label || 'Register'
    }
    return 'Register'
  }
  if (activeTab.value === 'data_posting') {
    if (activeDataPostingView.value) {
      const item = dataPostingDropdownItems.find((i) => i.key === activeDataPostingView.value)
      return item?.label || 'Data Posting'
    }
    return 'Data Posting'
  }
  if (activeTab.value === 'payroll') {
    if (activePayrollView.value) {
      const item = payrollDropdownItems.find((i) => i.key === activePayrollView.value)
      return item?.label || 'Payroll'
    }
    return 'Payroll'
  }
  const t = tabs.find((tab) => tab.key === activeTab.value)
  return t?.label || ''
})

function isDropdownOpen(tabKey) {
  if (tabKey === 'register') return showRegisterDropdown.value
  if (tabKey === 'advanced_register') return showAdvancedRegisterDropdown.value
  if (tabKey === 'data_posting') return showDataPostingDropdown.value
  if (tabKey === 'payroll') return showPayrollDropdown.value
  return false
}

function getDropdownItems(tabKey) {
  if (tabKey === 'register') return visibleRegisterDropdownItems.value
  if (tabKey === 'advanced_register') return visibleAdvancedRegisterDropdownItems.value
  if (tabKey === 'data_posting') return visibleDataPostingDropdownItems.value
  if (tabKey === 'payroll') return visiblePayrollDropdownItems.value
  const tab = tabs.find((t) => t.key === tabKey)
  return tab?.items || []
}

function closeAllDropdowns() {
  showRegisterDropdown.value = false
  showAdvancedRegisterDropdown.value = false
  showDataPostingDropdown.value = false
  showPayrollDropdown.value = false
}

function handleGlobalPointerDown(e) {
  const el = tabsBarRef.value
  if (!el) return
  if (el.contains(e.target)) return
  closeAllDropdowns()
}

function selectTab(key) {
  activeTab.value = key
  activeRegisterView.value = null
  activeAdvancedRegisterView.value = null
  activeDataPostingView.value = null
  activePayrollView.value = null
  closeAllDropdowns()
}

function toggleDropdownTab(tabKey) {
  if (tabKey === 'register') {
    activeTab.value = 'register'
    showRegisterDropdown.value = !showRegisterDropdown.value
    showAdvancedRegisterDropdown.value = false
    showDataPostingDropdown.value = false
    showPayrollDropdown.value = false
    activePayrollView.value = null
  } else if (tabKey === 'advanced_register') {
    activeTab.value = 'advanced_register'
    showAdvancedRegisterDropdown.value = !showAdvancedRegisterDropdown.value
    showRegisterDropdown.value = false
    showDataPostingDropdown.value = false
    showPayrollDropdown.value = false
    activeRegisterView.value = null
    activePayrollView.value = null
    if (!showAdvancedRegisterDropdown.value) activeAdvancedRegisterView.value = null
  } else if (tabKey === 'data_posting') {
    activeTab.value = 'data_posting'
    showDataPostingDropdown.value = !showDataPostingDropdown.value
    showRegisterDropdown.value = false
    showAdvancedRegisterDropdown.value = false
    showPayrollDropdown.value = false
    activeRegisterView.value = null
    activeAdvancedRegisterView.value = null
    activePayrollView.value = null
    if (!showDataPostingDropdown.value) activeDataPostingView.value = null
  } else if (tabKey === 'payroll') {
    activeTab.value = 'payroll'
    showPayrollDropdown.value = !showPayrollDropdown.value
    showRegisterDropdown.value = false
    showAdvancedRegisterDropdown.value = false
    showDataPostingDropdown.value = false
    activeRegisterView.value = null
    activeAdvancedRegisterView.value = null
    activeDataPostingView.value = null
    if (!showPayrollDropdown.value) activePayrollView.value = null
  }
}

function onDropdownItemClick(tabKey, item) {
  if (isDropdownItemDisabled(tabKey, item)) return
  if (tabKey === 'register' && item && typeof item === 'object' && item.key) {
    activeTab.value = 'register'
    activeRegisterView.value = item.key
    activeAdvancedRegisterView.value = null
    activeDataPostingView.value = null
    activePayrollView.value = null
  } else if (tabKey === 'advanced_register' && item && typeof item === 'object' && item.key) {
    activeTab.value = 'advanced_register'
    activeAdvancedRegisterView.value = item.key
    activeRegisterView.value = null
    activeDataPostingView.value = null
    activePayrollView.value = null
  } else if (tabKey === 'data_posting' && item && typeof item === 'object' && item.key) {
    activeTab.value = 'data_posting'
    activeDataPostingView.value = item.key
    activeRegisterView.value = null
    activeAdvancedRegisterView.value = null
    activePayrollView.value = null
  } else if (tabKey === 'payroll' && item && typeof item === 'object' && item.key) {
    activeTab.value = 'payroll'
    activePayrollView.value = item.key
    activeRegisterView.value = null
    activeAdvancedRegisterView.value = null
    activeDataPostingView.value = null
  }
  closeAllDropdowns()
}

onMounted(() => {
  document.addEventListener('pointerdown', handleGlobalPointerDown)
  loadCompanyProfile()
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleGlobalPointerDown)
})
</script>

<style scoped>
@media (max-width: 767px) {
  .finance-tabs-scroll {
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }

  .finance-tabs-scroll::-webkit-scrollbar {
    height: 4px;
  }

  .finance-tabs-scroll::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 4px;
  }
}
</style>
